import { MaterialCheckinPassQR } from "../../../common/components/MaterialCheckinPassQR";
import { useBookingPass } from "../hooks/useBookingPass";
import { CHECKIN_PASS_REFUSAL, checkinPassRefusal } from "../../../common/checkinPassRefusal";
import CloseRounded from "@mui/icons-material/CloseRounded";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

/**
 * The "Show my check-in QR" dialog for a booking. Available to ANY participant —
 * the host OR an accepted guest (the pass is NOT host-only; the backend authorizes
 * it). Mints the pass via useBookingPass(bookingId) and renders the SHARED
 * CheckinPassQR (the scannable QR + mm:ss countdown) — all server truth, never
 * fabricated. The signed credential is encoded in the QR only.
 *
 * WAI-ARIA modal: role="dialog" + aria-modal, focus trapped + moved inside on open
 * (mounted only while open, so the trap is always active), Escape + scrim dismiss.
 * Honest states: an in-flight pass shows a "Generating…" status (no fabricated
 * code); a 404/403/409 rejection surfaces an honest error with NO QR rendered. The
 * refresh path re-fetches the pass (refetch), so the displayed token never goes
 * stale.
 */
export function BookingPassDialog({
  bookingId,
  onClose,
}: {
  bookingId: string;
  onClose: () => void;
}) {
  const passQ = useBookingPass(bookingId, true);
  // Checked in, over or cancelled: no new QR, and nothing to retry (#2691).
  const refusal = passQ.isError ? checkinPassRefusal(passQ.error) : null;

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ pr: 7 }}>
        Your check-in pass
        <IconButton
          type="button"
          onClick={onClose}
          aria-label="Close"
          title="Close"
          autoFocus
          sx={{ position: "absolute", right: 12, top: 12 }}
        >
          <CloseRounded />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {refusal ? (
          <Alert severity="info" role="status">
            <strong>{CHECKIN_PASS_REFUSAL[refusal].label}.</strong>{" "}
            {CHECKIN_PASS_REFUSAL[refusal].message}
          </Alert>
        ) : passQ.isError ? (
          <Alert severity="error" role="alert">
            <Stack spacing={1} sx={{ alignItems: "flex-start" }}>
              {/* ONE message matches the honest-error assertion; the refresh
                control's label is deliberately worded to not double-match. */}
              <Typography>
                We couldn&apos;t get your check-in pass. The booking may not be
                confirmed yet.
              </Typography>
              <Button
                type="button"
                variant="outlined"
                color="inherit"
                size="small"
                onClick={() => void passQ.refetch()}
              >
                Get a new pass
              </Button>
            </Stack>
          </Alert>
        ) : passQ.data ? (
          <MaterialCheckinPassQR
            token={passQ.data.token}
            expiresAt={passQ.data.expires_at}
            label="Court pass"
            onRefresh={() => void passQ.refetch()}
          />
        ) : (
          <Typography color="text.secondary" role="status">
            Generating your check-in pass…
          </Typography>
        )}
      </DialogContent>
    </Dialog>
  );
}
